import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import {
  getZones,
  getZoneCurrentRestaurants,
  getZoneSpotlights,
  restaurants as restaurantsQuery,
  saveZoneSpotlight
} from '../../apollo'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import {
  Box,
  Typography,
  Input,
  Button,
  Alert,
  Grid,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  CircularProgress,
  IconButton
} from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import CloseIcon from '@mui/icons-material/Close'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import FormCardHeader from '../FormCardHeader'

// The theme's text.primary is white, so menu items need an explicit colour
// or they render invisible on the near-white menu paper.
const MENU_PROPS = {
  PaperProps: {
    sx: {
      '& .MuiMenuItem-root': {
        color: '#212121'
      },
      '& .MuiMenuItem-root.Mui-selected': {
        color: '#212121'
      }
    }
  }
}

const GET_ZONES = gql`
  ${getZones}
`
const GET_RESTAURANTS = gql`
  ${restaurantsQuery}
`
const GET_ZONE_SPOTLIGHTS = gql`
  ${getZoneSpotlights}
`
const GET_ZONE_CURRENT_RESTAURANTS = gql`
  ${getZoneCurrentRestaurants}
`
const SAVE_ZONE_SPOTLIGHT = gql`
  ${saveZoneSpotlight}
`

// Empty value means the rail carries stores of any shop type
const SHOP_TYPES = ['restaurant', 'grocery']

// One draggable row. The whole row is the handle, so a store can be grabbed
// anywhere except the remove button.
function SortableRow({ id, position, restaurant, onRemove, classes }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1 : 0
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={classes.row}
      {...attributes}
      {...listeners}>
      <DragIndicatorIcon fontSize="small" className={classes.dragHandle} />
      <span className={classes.position}>{position}</span>
      {restaurant?.image ? (
        <img src={restaurant.image} alt="" className={classes.rowImage} />
      ) : (
        <Box className={classes.rowImagePlaceholder} />
      )}
      <Box className={classes.rowText}>
        <Typography className={classes.rowTitle}>
          {restaurant?.name || id}
        </Typography>
        <Typography className={classes.rowSubtitle}>
          {restaurant?.shopType || ''}
          {restaurant && restaurant.isActive === false ? ' • inactive' : ''}
        </Typography>
      </Box>
      <Button
        type="button"
        // Pointer down would start a drag before the click lands
        onPointerDown={event => event.stopPropagation()}
        onClick={() => onRemove(id)}
        className={classes.removeButton}>
        <CloseIcon fontSize="small" />
      </Button>
    </Box>
  )
}

function ZoneSpotlight(props) {
  const { t, editTarget, onClose } = props
  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  // Opened from a table row rather than sitting on the page. The selectors are
  // seeded from the row so the dialog never flashes an empty form first.
  const isEditing = Boolean(editTarget)

  const [zone, setZone] = useState(editTarget?.zone ?? '')
  const [shopType, setShopType] = useState(editTarget?.shopType ?? '')
  const [title, setTitle] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [selected, setSelected] = useState([])
  const [picker, setPicker] = useState('')
  // Where the list on screen came from, so the admin knows whether they are
  // editing a saved order or a copy of what the app shows today.
  const [source, setSource] = useState('')
  const [mainError, mainErrorSetter] = useState('')
  const [success, successSetter] = useState('')

  const { data: zonesData } = useQuery(GET_ZONES)
  const { data: restaurantsData } = useQuery(GET_RESTAURANTS)
  const { data: spotlightsData, loading: spotlightsLoading } = useQuery(
    GET_ZONE_SPOTLIGHTS
  )
  const { data: currentData, loading: currentLoading } = useQuery(
    GET_ZONE_CURRENT_RESTAURANTS,
    {
      variables: { zone, shopType: shopType || null },
      skip: !zone,
      fetchPolicy: 'cache-and-network'
    }
  )

  const zones = zonesData?.zones?.filter(item => item.isActive) ?? []
  const allRestaurants = restaurantsData?.restaurants ?? []

  const restaurantsById = useMemo(() => {
    const map = new Map(allRestaurants.map(item => [item._id, item]))
    // The live listing can include a store the global list has not loaded
    ;(currentData?.zoneCurrentRestaurants ?? []).forEach(item => {
      if (!map.has(item._id)) map.set(item._id, item)
    })
    return map
  }, [allRestaurants, currentData])

  const currentListing = useMemo(
    () => (currentData?.zoneCurrentRestaurants ?? []).map(item => item._id),
    [currentData]
  )

  const saved = useMemo(
    () =>
      (spotlightsData?.zoneSpotlights ?? []).find(
        item => item.zone?._id === zone && (item.shopType || '') === shopType
      ),
    [spotlightsData, zone, shopType]
  )

  // Filled in once per zone and shop type. Without this guard a background
  // refetch would re-run the effect and throw away a reorder in progress.
  const loadedKey = useRef('')

  // A saved order wins, because that is what the app is serving. With nothing
  // saved yet the list starts as the live listing, so the admin is always
  // reordering something real rather than building from an empty box.
  useEffect(() => {
    if (!zone) {
      loadedKey.current = ''
      setSource('')
      return
    }

    const key = `${zone}|${shopType}`
    if (loadedKey.current === key) return
    // Wait for both answers before deciding which one to show
    if (spotlightsLoading || currentLoading) return

    if (saved) {
      setSelected(saved.restaurants ?? [])
      setTitle(saved.title ?? '')
      setIsActive(saved.isActive)
      setSource('saved')
    } else {
      setSelected(currentListing)
      setTitle('')
      setIsActive(true)
      setSource(currentListing.length ? 'current' : 'empty')
    }
    loadedKey.current = key
  }, [zone, shopType, saved, currentListing, currentLoading, spotlightsLoading])

  // Nothing is on screen until the saved row has been read back, so the dialog
  // cannot be edited while it still holds a half-loaded list.
  // Guarded on zone as well: a row with no zone left would otherwise sit on a
  // spinner forever, since the loader effect never runs without one.
  const formLoading = isEditing && Boolean(zone) && !source

  const [mutate, { loading }] = useMutation(SAVE_ZONE_SPOTLIGHT, {
    refetchQueries: [{ query: GET_ZONE_SPOTLIGHTS }],
    onCompleted: () => {
      mainErrorSetter('')
      // In the dialog the confirmation belongs next to the table the admin is
      // sent back to, not on a form that is about to disappear.
      if (isEditing) {
        onClose?.(t('ZoneSpotlightSaved'))
        return
      }
      successSetter(t('ZoneSpotlightSaved'))
    },
    onError: error => {
      let message = ''
      try {
        message = error.graphQLErrors[0].message
      } catch (err) {
        message = t('ActionFailedTryAgain')
      }
      successSetter('')
      mainErrorSetter(message)
    }
  })

  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so clicks still register
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const onDragEnd = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setSelected(current => {
      const from = current.indexOf(active.id)
      const to = current.indexOf(over.id)
      if (from === -1 || to === -1) return current
      return arrayMove(current, from, to)
    })
  }

  const onAdd = id => {
    if (!id || selected.includes(id)) return
    setSelected(current => [...current, id])
    setPicker('')
  }

  const onRemove = id => setSelected(current => current.filter(x => x !== id))

  // Stores of the wrong shop type would never render in the rail
  const pickable = allRestaurants
    .filter(item => !selected.includes(item._id))
    .filter(item => !shopType || (item.shopType || '') === shopType)

  return (
    <Box container className={classes.container}>
      <Box className={classes.headerRow}>
        <Box
          item
          className={isEditing ? classes.headingBlack : classes.heading}>
          <Typography
            variant="h6"
            className={isEditing ? classes.textWhite : classes.text}>
            {/* Says which of the two jobs the form is doing, since one form
                both creates and edits. */}
            {isEditing ? t('EditZoneSpotlight') : t('ZoneSpotlight')}
          </Typography>
        </Box>
        {isEditing ? (
          <IconButton
            aria-label="close"
            title={t('Cancel')}
            className={classes.closeButton}
            onClick={() => onClose?.()}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
      <Box className={classes.form}>
        <Typography className={classes.helpText}>
          {t('ZoneSpotlightHelp')}
        </Typography>

        <Box className={globalClasses.flexRow}>
          <Grid container columnSpacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography className={classes.labelText}>{t('Zone')}</Typography>
              <Select
                id="input-zone-spotlight-zone"
                value={zone}
                displayEmpty
                onChange={event => setZone(event.target.value)}
                disabled={isEditing && Boolean(zone)}
                className={globalClasses.input}
                MenuProps={MENU_PROPS}
                style={{ marginTop: -1 }}>
                <MenuItem value="">{t('SelectZone')}</MenuItem>
                {zones.map(item => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography className={classes.labelText}>
                {t('Shop type')}
              </Typography>
              <Select
                id="input-zone-spotlight-shop-type"
                value={shopType}
                displayEmpty
                onChange={event => setShopType(event.target.value)}
                disabled={isEditing}
                className={globalClasses.input}
                MenuProps={MENU_PROPS}
                style={{ marginTop: -1 }}>
                <MenuItem value="">{t('AllShopTypes')}</MenuItem>
                {SHOP_TYPES.map(slug => (
                  <MenuItem key={slug} value={slug}>
                    {slug}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography className={classes.labelText}>
                {t('RailTitle')} ({t('Optional')})
              </Typography>
              <Input
                style={{ marginTop: -1 }}
                id="input-zone-spotlight-title"
                name="input-zone-spotlight-title"
                placeholder={t('PHRailTitle')}
                type="text"
                value={title}
                onChange={event => setTitle(event.target.value)}
                disableUnderline
                className={globalClasses.input}
              />
            </Grid>
          </Grid>
        </Box>

        {formLoading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress size={22} />
            <Typography className={classes.sourceText}>
              {t('LoadingSpotlight')}
            </Typography>
          </Box>
        ) : zone ? (
          <>
            <Box className={globalClasses.flexRow} mt={1}>
              <Grid container columnSpacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography className={classes.labelText}>
                    {t('AddStore')}
                  </Typography>
                  <Select
                    id="input-zone-spotlight-picker"
                    value={picker}
                    displayEmpty
                    onChange={event => onAdd(event.target.value)}
                    className={globalClasses.input}
                    MenuProps={MENU_PROPS}
                    style={{ marginTop: -1 }}>
                    <MenuItem value="">{t('SelectStore')}</MenuItem>
                    {pickable.map(item => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isActive}
                        onChange={event => setIsActive(event.target.checked)}
                      />
                    }
                    label={t('Status')}
                    style={{ marginLeft: '4%', marginTop: 28 }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box mt={2} className={classes.listWrapper}>
              <Box className={classes.sourceRow}>
                <Typography className={classes.sourceText}>
                  {source === 'saved'
                    ? t('EditingSavedOrder')
                    : source === 'current'
                    ? t('EditingCurrentListing')
                    : ''}
                </Typography>
                <Box>
                  <Button
                    type="button"
                    size="small"
                    disabled={currentLoading || !currentListing.length}
                    onClick={() => {
                      setSelected(currentListing)
                      setSource('current')
                    }}
                    className={classes.secondaryButton}>
                    {t('UseCurrentListing')}
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    onClick={() => {
                      setSelected([])
                      setSource('empty')
                    }}
                    className={classes.secondaryButton}>
                    {t('StartEmptyList')}
                  </Button>
                </Box>
              </Box>
              {selected.length ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onDragEnd}>
                  <SortableContext
                    items={selected}
                    strategy={verticalListSortingStrategy}>
                    {selected.map((id, index) => (
                      <SortableRow
                        key={id}
                        id={id}
                        position={index + 1}
                        restaurant={restaurantsById.get(id)}
                        onRemove={onRemove}
                        classes={classes}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : currentLoading ? (
                <Box className={classes.loadingBox}>
                  <CircularProgress size={18} />
                </Box>
              ) : (
                <Typography className={classes.emptyText}>
                  {t('NoStoresPinned')}
                </Typography>
              )}
            </Box>

            <Box mt={2}>
              <Button
                className={globalClasses.button}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
                disabled={loading || currentLoading}
                onClick={e => {
                  e.preventDefault()
                  if (loading) return
                  mutate({
                    variables: {
                      zoneSpotlightInput: {
                        zone,
                        shopType,
                        title: title.trim(),
                        // Array order is the display order in the app
                        restaurants: selected,
                        isActive
                      }
                    }
                  })
                }}>
                {loading ? t('Saving') : t('Save')}
              </Button>
            </Box>
          </>
        ) : (
          <Typography className={classes.emptyText}>
            {t('SelectZoneToStart')}
          </Typography>
        )}

        <Box mt={2}>
          {success && (
            <Alert
              className={globalClasses.alertSuccess}
              variant="filled"
              severity="success">
              {success}
            </Alert>
          )}
          {mainError && (
            <Alert
              className={globalClasses.alertError}
              variant="filled"
              severity="error">
              {mainError}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default withTranslation()(ZoneSpotlight)
